import {
  activeShipmentPipelineStatuses,
  completedShipmentPipelineStatuses,
  customsShipmentPipelineStatuses,
  getNextShipmentPipelineStatusLabel,
  getShipmentPipelineStatusLabel,
  inTransitShipmentPipelineStatuses,
  ShipmentPipelineStatus,
  warehouseShipmentPipelineStatuses
} from '@/features/Shipment/model/shipmentPipeline';
import { formatValue } from '@/lib/formatters';
import { GetShipmentCountsResponse } from '@/features/Shipment/api';
import { ShipmentListItem as ShipmentListItemApi } from '@/features/Shipment/api/types';

const SHIPMENTS_LIST_MEMORY_KEY = 'shipments-list-memory';
export const SHIPMENTS_PAGE_SIZE = 5;

export type ShipmentsFilterQueryValue = 'active' | 'at_customs' | 'at_warehouse' | 'completed' | 'in_transit';

export type ShipmentsTabValue = 'active' | 'completed' | 'customs' | 'inTransit' | 'warehouse';

export interface ShipmentListItem {
  displayDate: string | null;
  id: string;
  nextStatusLabel: string | null;
  number: string;
  shipmentNumber: string | null;
  status: string | null;
  statusLabel: string;
  supplierName: string;
}

export interface ShipmentsListMemory {
  loadedPages: number;
  scrollY: number;
  shipmentId: string;
}

type ShipmentsListMemoryStore = Record<string, ShipmentsListMemory>;

export const SHIPMENTS_TABS: Array<{
  label: string;
  value: ShipmentsTabValue;
}> = [
  {
    label: 'Все активные',
    value: 'active'
  },
  {
    label: 'В пути',
    value: 'inTransit'
  },
  {
    label: 'Таможня',
    value: 'customs'
  },
  {
    label: 'На складе',
    value: 'warehouse'
  },
  {
    label: 'Завершено',
    value: 'completed'
  }
];

const SHIPMENTS_FILTER_TO_STATUSES: Record<ShipmentsFilterQueryValue, ShipmentPipelineStatus[]> = {
  active: activeShipmentPipelineStatuses,
  at_customs: customsShipmentPipelineStatuses,
  at_warehouse: warehouseShipmentPipelineStatuses,
  completed: completedShipmentPipelineStatuses,
  in_transit: inTransitShipmentPipelineStatuses
};

const SHIPMENTS_FILTER_TO_TAB: Record<ShipmentsFilterQueryValue, ShipmentsTabValue> = {
  active: 'active',
  at_customs: 'customs',
  at_warehouse: 'warehouse',
  completed: 'completed',
  in_transit: 'inTransit'
};

const SHIPMENTS_TAB_TO_FILTER: Record<ShipmentsTabValue, ShipmentsFilterQueryValue> = {
  active: 'active',
  completed: 'completed',
  customs: 'at_customs',
  inTransit: 'in_transit',
  warehouse: 'at_warehouse'
};

const getMemoryStore = (): ShipmentsListMemoryStore => {
  try {
    const stored = sessionStorage.getItem(SHIPMENTS_LIST_MEMORY_KEY);

    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

export const clearShipmentsListMemory = (key: string): void => {
  try {
    const memoryStore = getMemoryStore();

    delete memoryStore[key];
    sessionStorage.setItem(SHIPMENTS_LIST_MEMORY_KEY, JSON.stringify(memoryStore));
  } catch {
    return;
  }
};

export const getShipmentsFilterFromQuery = (filter: string | null): ShipmentsFilterQueryValue => {
  if (filter === 'at_customs') return 'at_customs';
  if (filter === 'at_warehouse') return 'at_warehouse';
  if (filter === 'completed') return 'completed';
  if (filter === 'in_transit') return 'in_transit';

  return 'active';
};

export const getShipmentsFilterFromTab = (tab: ShipmentsTabValue): ShipmentsFilterQueryValue => {
  return SHIPMENTS_TAB_TO_FILTER[tab];
};

export const getShipmentsListMemory = (key: string): ShipmentsListMemory | null => {
  const memoryStore = getMemoryStore();

  return memoryStore[key] ?? null;
};

export const getShipmentsListMemoryKey = (
  path: string,
  filter: ShipmentsFilterQueryValue
): string => {
  return `${path}?filter=${filter}`;
};

export const getShipmentsStatusesFromFilter = (
  filter: ShipmentsFilterQueryValue
): ShipmentPipelineStatus[] => {
  return SHIPMENTS_FILTER_TO_STATUSES[filter];
};

export const getShipmentsTabFromFilter = (filter: string | null): ShipmentsTabValue => {
  const normalizedFilter = getShipmentsFilterFromQuery(filter);

  return SHIPMENTS_FILTER_TO_TAB[normalizedFilter];
};

export const mapShipmentCountsToTabCounts = (
  counts?: GetShipmentCountsResponse
): Record<ShipmentsTabValue, number> => {
  return {
    active: counts?.active_count ?? 0,
    completed: counts?.completed_count ?? 0,
    customs: counts?.customs_count ?? 0,
    inTransit: counts?.in_transit_count ?? 0,
    warehouse: counts?.warehouse_count ?? 0
  };
};

export const mapShipmentListItemApiToShipmentListItem = (item: ShipmentListItemApi): ShipmentListItem => {
  const invoiceNumber = item.invoice_number?.trim();
  const shipmentNumber = item.number?.trim();
  const supplierName = item.supplier_name?.trim();

  return {
    displayDate: item.invoice_date ?? null,
    id: item.shipment_id,
    nextStatusLabel: getNextShipmentPipelineStatusLabel(item.status),
    number: formatValue({ value: invoiceNumber }),
    shipmentNumber: shipmentNumber || null,
    status: item.status,
    statusLabel: getShipmentPipelineStatusLabel(item.status),
    supplierName: formatValue({ value: supplierName })
  };
};

export const saveShipmentsListMemory = (key: string, memory: ShipmentsListMemory): void => {
  try {
    const memoryStore = getMemoryStore();

    memoryStore[key] = memory;
    sessionStorage.setItem(SHIPMENTS_LIST_MEMORY_KEY, JSON.stringify(memoryStore));
  } catch {
    return;
  }
};